export const PERIODS = ['1M', '3M', '6M', 'YTD', '1Y', 'ALL'];

export function parseUTCDate(raw) {
  try {
    if (!raw) return new Date();
    const v = typeof raw === 'object' && raw.$date ? raw.$date : raw;
    if (typeof v === 'object' && v && 'year' in v) {
      return new Date(Date.UTC(v.year, (v.month || 1) - 1, v.day || 1));
    }
    const s = String(v);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    }
  } catch {}
  return new Date();
}

export function toUtcTimestamp(raw) {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const d = parseUTCDate(raw);
  return Math.floor(d.getTime() / 1000);
}

export function mapChartBars(rows) {
  const src = Array.isArray(rows) ? rows : [];
  return src
    .map((row) => {
      const time = toUtcTimestamp(row?.time ?? row?.date);
      const open = Number(row?.open ?? row?.open_price ?? 0);
      const high = Number(row?.high ?? row?.high_price ?? 0);
      const low = Number(row?.low ?? row?.low_price ?? 0);
      const close = Number(row?.close ?? row?.close_price ?? 0);
      const volume = Number(row?.volume ?? 0);
      return {
        ...row,
        time,
        open,
        high,
        low,
        close,
        volume,
        value: close,
        raw: row,
      };
    })
    .filter((bar) => Number.isFinite(bar.time) && bar.time > 0)
    .sort((a, b) => a.time - b.time);
}

export function flattenPredictionPoints(predictions) {
  if (!predictions) return [];
  const docs = Array.isArray(predictions.docs) ? predictions.docs : [];
  const inline = Array.isArray(predictions.preds) ? predictions.preds : [];
  const points = [];

  for (const doc of docs) {
    const preds = Array.isArray(doc?.preds) ? doc.preds : [];
    for (const p of preds) {
      points.push({
        time: toUtcTimestamp(p?.time ?? p?.date),
        value: Number(p?.value ?? p?.predicted_price ?? p?.y),
      });
    }
  }
  for (const p of inline) {
    points.push({
      time: toUtcTimestamp(p?.time ?? p?.date),
      value: Number(p?.value ?? p?.predicted_price ?? p?.y),
    });
  }

  return points
    .filter((p) => Number.isFinite(p.time) && Number.isFinite(p.value) && p.value > 0)
    .sort((a, b) => a.time - b.time);
}

export function latestPredictionPoints(predictions) {
  const docs = Array.isArray(predictions?.docs) ? [...predictions.docs] : [];
  if (!docs.length) return flattenPredictionPoints(predictions);

  docs.sort((a, b) => {
    const aDate = new Date(a?.date || a?.created_at || 0).getTime();
    const bDate = new Date(b?.date || b?.created_at || 0).getTime();
    return bDate - aDate;
  });

  const preds = Array.isArray(docs[0]?.preds) ? docs[0].preds : [];
  return preds
    .map((p) => ({
      time: toUtcTimestamp(p?.time ?? p?.date),
      value: Number(p?.value ?? p?.predicted_price ?? p?.y),
    }))
    .filter((p) => Number.isFinite(p.time) && Number.isFinite(p.value) && p.value > 0)
    .sort((a, b) => a.time - b.time);
}

export function computeVisibleRangeForPeriod(mapped, predictions, period) {
  const rows = Array.isArray(mapped) ? mapped : [];
  if (!rows.length) return null;

  const first = Number(rows[0].time);
  const last = Number(rows[rows.length - 1].time);
  if (!Number.isFinite(first) || !Number.isFinite(last)) return null;

  const predictionPoints = flattenPredictionPoints(predictions);
  const maxPredTs = predictionPoints.length ? Number(predictionPoints[predictionPoints.length - 1].time) : null;
  const to = Math.max(last, maxPredTs || last) + 3 * 24 * 60 * 60;
  const clampFrom = (fromTs) => Math.max(first, Math.min(fromTs, to));

  if (period === 'ALL') {
    return { from: first, to };
  }

  if (period === 'YTD') {
    const lastDate = new Date(last * 1000);
    const ytdStart = Date.UTC(lastDate.getUTCFullYear(), 0, 1) / 1000;
    return { from: clampFrom(Math.floor(ytdStart)), to };
  }

  const monthMap = { '1M': 1, '3M': 3, '6M': 6, '1Y': 12 };
  const months = monthMap[period] || 0;
  if (!months) return { from: first, to };

  const d = new Date(last * 1000);
  d.setUTCMonth(d.getUTCMonth() - months);
  const from = Math.floor(d.getTime() / 1000);
  return { from: clampFrom(from), to };
}

export function findBarForSignalTimestamp(mapped, signalTs) {
  const rows = Array.isArray(mapped) ? mapped : [];
  if (!rows.length || !Number.isFinite(signalTs)) return null;

  const sameUtcDate = (aTs, bTs) => {
    const a = new Date(aTs * 1000);
    const b = new Date(bTs * 1000);
    return (
      a.getUTCFullYear() === b.getUTCFullYear() &&
      a.getUTCMonth() === b.getUTCMonth() &&
      a.getUTCDate() === b.getUTCDate()
    );
  };

  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (sameUtcDate(Number(rows[i].time), signalTs)) return rows[i];
    if (Number(rows[i].time) < signalTs) break;
  }

  for (let i = 0; i < rows.length; i += 1) {
    if (Number(rows[i].time) >= signalTs) return rows[i];
  }

  return rows[rows.length - 1] || null;
}

export function buildTradeSignalMarkers(mapped, tradeSignals) {
  if (!Array.isArray(tradeSignals)) return [];
  const markers = [];
  for (const signal of tradeSignals) {
    if (!signal || (signal.action !== 'BUY' && signal.action !== 'SELL')) continue;
    const ts = toUtcTimestamp(signal.date || signal.created_at);
    const bar = findBarForSignalTimestamp(mapped, ts);
    if (!bar) continue;
    markers.push({
      time: Number(bar.time),
      position: signal.action === 'BUY' ? 'belowBar' : 'aboveBar',
      color: signal.action === 'BUY' ? '#10b981' : '#ef4444',
      shape: signal.action === 'BUY' ? 'arrowUp' : 'arrowDown',
      text: signal.action,
    });
  }
  return markers.sort((a, b) => a.time - b.time);
}
